import { useEffect, useRef, useState } from 'react';
import { Form, InputGroup, Spinner } from 'react-bootstrap';
import { patientApi } from '../../api';
import { formatAge, titleCase } from '../../utils/format';

/**
 * The "search by name or number" box. Used by reception to pull up a returning
 * patient and by the doctor to look someone up mid-clinic.
 */
export default function PatientSearch({ onSelect, autoFocus, placeholder = 'Search by name, mobile or patient ID' }) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef(null);

  // Debounced so a fast typist does not fire a request per keystroke.
  useEffect(() => {
    const query = term.trim();
    if (query.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await patientApi.list({ search: query, limit: 8 });
        setResults(res.data);
        setOpen(true);
        setHighlight(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    function onClickAway(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  function choose(patient) {
    onSelect(patient);
    setTerm('');
    setResults([]);
    setOpen(false);
  }

  function onKeyDown(e) {
    if (!open || !results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(results[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="position-relative" ref={boxRef}>
      <InputGroup>
        <InputGroup.Text className="bg-white">
          <i className="bi bi-search" />
        </InputGroup.Text>
        <Form.Control
          value={term}
          autoFocus={autoFocus}
          placeholder={placeholder}
          onChange={(e) => setTerm(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {loading && (
          <InputGroup.Text className="bg-white">
            <Spinner animation="border" size="sm" />
          </InputGroup.Text>
        )}
      </InputGroup>

      {open && (
        <div className="suggest-list">
          {results.length === 0 ? (
            <div className="px-3 py-2 text-muted small">No patient found for "{term}"</div>
          ) : (
            results.map((p, i) => (
              <button
                type="button"
                key={p._id}
                className={i === highlight ? 'active' : ''}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => choose(p)}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">{p.name}</span>
                  <span className="text-muted small">{p.patientId}</span>
                </div>
                <div className="text-muted small">
                  <i className="bi bi-telephone me-1" />
                  {p.mobile}
                  <span className="mx-2">|</span>
                  {titleCase(p.gender)}, {formatAge(p)}
                  {p.totalVisits > 0 && (
                    <>
                      <span className="mx-2">|</span>
                      {p.totalVisits} visit{p.totalVisits > 1 ? 's' : ''}
                    </>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
